import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileText, Minus, Plus, ShoppingCart, Trash2, UserCheck, Zap } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { ordersAPI, partnerAPI, publicAPI } from "@/services/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { PRODUCT_TYPES, formatCurrency } from "@/utils/constants";
import { getErrorMessage } from "@/utils/errorHelpers";

export default function CartPage({ user }) {
  const { items, removeItem, updateQuantity, clearCart, totalPrice, totalItems } = useCart();
  const [demarcheurId, setDemarcheurId] = useState("");
  const [signerName, setSignerName] = useState(user?.full_name || "");
  const [signatureAccepted, setSignatureAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [rates, setRates] = useState({ mc_to_usd_rate: 1, usd_to_cdf_rate: 2800 });
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const containsActivation = Array.isArray(items) && items.some((item) => item.product_type === "activation" || item.product_id === "PROD_ACTIVATION");
  const mcToUsd = Number(rates.mc_to_usd_rate || 0);
  const usdToCdf = Number(rates.usd_to_cdf_rate || 0);
  const totalUsd = totalPrice * mcToUsd;
  const totalCdf = totalUsd * usdToCdf;

  useEffect(() => {
    publicAPI.getPlatformSettings()
      .then((res) => setRates((prev) => ({ ...prev, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  const handleCheckout = async () => {
    if (containsActivation && !user?.is_verified) {
      toast.error("Vous devez valider votre KYC avant de demander une activation partenaire");
      navigate("/verification");
      return;
    }

    if (items.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }

    if (!signerName.trim() || !signatureAccepted) {
      toast.error("veuillez completer la signature metier de commande");
      return;
    }

    setLoading(true);
    try {
      const response = await ordersAPI.createBatch({
        items: items.map((item) => ({ product_id: item.product_id, quantity: item.quantity || 1 })),
        demarcheur_id: demarcheurId.trim() || null,
        business_signature: {
          accepted: signatureAccepted,
          signer_name: signerName.trim(),
          buyer_account_id: user?.id,
          buyer_level: user?.partner_level || "",
        },
      });

      const { orders } = response.data;
      if (!orders || !Array.isArray(orders) || orders.length === 0) {
        toast.error("Aucune commande creee");
        return;
      }

      const receiptUrl = ordersAPI.getBatchReceiptUrl(orders.map((order) => order.order_id));
      window.open(receiptUrl, "_blank");
      toast.success(`${orders.length} session(s) de commande creee(s)`);
      clearCart();
      navigate("/orders");
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la creation des commandes"));
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePAR = async () => {
    setActivating(true);
    try {
      const response = await partnerAPI.requestActivation();
      toast.success(response.data.message || "Demande d'activation envoyee");
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'activation"));
    } finally {
      setActivating(false);
    }
  };

  if (!items) return <LoadingState />;

  if (items.length === 0) {
    return (
      <PageShell className="flex min-h-[55vh] items-center justify-center text-center" data-testid="empty-cart-page">
        <div>
          <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-[#F3EFE9]">
            <ShoppingCart size={48} className="text-[#8A8F8C]" />
          </div>
          <h2 className="text-2xl font-bold text-[#2A4B46] font-['Outfit']">Votre panier est vide</h2>
          <Button onClick={() => navigate("/catalogue")} className="mt-6 bg-[#2A4B46] text-white" data-testid="browse-catalogue-button">
            Retour au catalogue
          </Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell data-testid="cart-page">
      <PageHeader title="Finaliser ma commande" description={`${totalItems} article(s) dans votre panier.`} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          {items.map((item) => (
            <CartItem
              key={item.product_id}
              item={item}
              updateQuantity={updateQuantity}
              removeItem={removeItem}
            />
          ))}
        </div>

        <div className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <Card className="overflow-hidden border-t-4 border-t-[#D4A373]">
            <CardHeader className="bg-[#2A4B46] text-white">
              <CardTitle className="text-lg">Resume de la transaction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-5 sm:pt-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium text-[#5C625F]">
                  <UserCheck size={16} /> ID du Demarcheur (optionnel)
                </Label>
                <Input
                  placeholder="Ex: ART243005"
                  value={demarcheurId}
                  onChange={(e) => setDemarcheurId(e.target.value.toUpperCase())}
                  data-testid="demarcheur-id-input"
                />
                <p className="text-xs leading-5 text-[#8A8F8C]">Si vide, le bonus reviendra automatiquement au compte ARTICO001.</p>
              </div>

              <div className="space-y-3 border-t border-[#E6E4E0] pt-4">
                <div className="flex justify-between text-sm text-[#5C625F]">
                  <span>Nombre d'articles</span>
                  <span>{totalItems}</span>
                </div>
                <div className="flex items-start justify-between gap-3 text-xl font-bold text-[#2A4B46] sm:text-2xl">
                  <span>Total a payer</span>
                  <span className="text-right" data-testid="cart-total">{formatCurrency(totalPrice)}</span>
                </div>
                {(mcToUsd > 0 || usdToCdf > 0) && (
                  <p className="text-right text-xs leading-5 text-[#6D7470]">
                    Equivalent indicatif: {formatUsd(totalUsd)} / {formatCdf(totalCdf)}
                  </p>
                )}
              </div>

              <div className="space-y-3 border-t border-[#E6E4E0] pt-4">
                <Label htmlFor="business_signer" className="text-sm font-medium text-[#5C625F]">
                  Signature metier
                </Label>
                <Input
                  id="business_signer"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Nom complet du signataire"
                  data-testid="business-signer-input"
                />
                <div className="flex items-start gap-2 rounded-xl bg-[#F9F7F4] p-3">
                  <Checkbox
                    checked={signatureAccepted}
                    onCheckedChange={(value) => setSignatureAccepted(!!value)}
                    data-testid="business-signature-checkbox"
                  />
                  <Label className="cursor-pointer text-xs leading-5 text-[#5C625F]">
                    Je certifie que cette commande reprend mon compte MMJ, le demarcheur indique, les produits demandes, le montant total en points-caisse, et qu'elle vaut signature numerique.
                  </Label>
                </div>
              </div>

              {!user?.is_verified && containsActivation && (
                <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3">
                  <AlertTriangle className="shrink-0 text-red-500" size={16} />
                  <p className="text-xs leading-5 text-red-700">Votre identite doit etre verifiee pour une activation partenaire. Les produits ordinaires restent disponibles.</p>
                </div>
              )}

              {!isAdmin && (
                <Button
                  className="h-12 w-full bg-[#D4A373] font-bold text-white hover:bg-[#B88E5B]"
                  onClick={handleCheckout}
                  disabled={loading || items.length === 0 || !signatureAccepted || !signerName.trim()}
                  data-testid="checkout-button"
                >
                  {loading ? "Creation des sessions..." : "valider et générer le PDF"}
                </Button>
              )}

              <Button variant="ghost" className="w-full text-red-600 hover:bg-red-50" onClick={clearCart} data-testid="clear-cart-button">
                Vider le panier
              </Button>
            </CardContent>
          </Card>

          {user?.role === "partner" && user?.is_verified && (
            <Card className="border-l-4 border-l-[#D4A373]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#2A4B46]">
                  <Zap size={20} className="text-[#D4A373]" /> Activation du compte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm leading-6 text-[#5C625F]">Activez votre compte partenaire. L'administrateur validera votre paiement.</p>
                <Button className="w-full bg-[#D4A373] text-white hover:bg-[#c49362]" onClick={handleActivatePAR} disabled={activating} data-testid="activate-par-button">
                  {activating ? "Envoi..." : "Demander l'activation"}
                </Button>
              </CardContent>
            </Card>
          )}

          <p className="text-center text-xs italic text-[#8A8F8C]">après validation, un bon de commande PDF s'ouvrira automatiquement.</p>
        </div>
      </div>
    </PageShell>
  );
}

function CartItem({ item, updateQuantity, removeItem }) {
  const isActivation = item.product_type === "activation";

  return (
    <Card className="transition-shadow hover:shadow-md" data-testid={`cart-item-${item.product_id}`}>
      <CardContent className="flex flex-col gap-4 pt-5 sm:flex-row sm:items-center sm:justify-between sm:pt-6">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isActivation ? "bg-amber-100" : "bg-green-100"}`}>
            {isActivation ? <Zap className="text-amber-600" size={22} /> : <FileText className="text-[#2A4B46]" size={22} />}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="line-clamp-2 font-bold text-[#2A4B46]">{item.title}</h3>
              <Badge className={isActivation ? "bg-amber-100 text-amber-700" : "bg-[#F3EFE9] text-[#2A4B46]"}>
                {isActivation ? "Activation MLM" : PRODUCT_TYPES[item.product_type]?.label}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-[#5C625F]">{formatCurrency(item.price_mc)} / unite</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <div className="flex items-center rounded-xl border border-[#E6E4E0] bg-white">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={() => updateQuantity(item.product_id, Math.max(1, (item.quantity || 1) - 1))} data-testid={`decrease-${item.product_id}`}>
              <Minus size={14} />
            </Button>
            <span className="w-10 text-center font-bold" data-testid={`quantity-${item.product_id}`}>{item.quantity || 1}</span>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-[#D4A373]" onClick={() => updateQuantity(item.product_id, (item.quantity || 1) + 1)} data-testid={`increase-${item.product_id}`}>
              <Plus size={14} />
            </Button>
          </div>

          <div className="min-w-24 text-right">
            <p className="font-bold text-[#2A4B46]">{formatCurrency(item.price_mc * (item.quantity || 1))}</p>
          </div>

          <Button variant="ghost" size="icon" className="rounded-xl text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => removeItem(item.product_id)} data-testid={`remove-${item.product_id}`}>
            <Trash2 size={19} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function formatUsd(value) {
  return `${Number(value || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function formatCdf(value) {
  return `${Number(value || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} CDF`;
}
